import { Suspense, useEffect } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useLoader } from "contexts";

/**
 * Shown while a lazily loaded page (its JS chunk) is downloading. It uses the
 * global loader instead of a spinner of its own: on a reload the chunk and
 * the page's data load together, and the loader counts both, so one spinner
 * shows until both are done.
 *
 * `compact`: no placeholder box (for a nested route, e.g. a dialog, whose
 * parent page stays on screen).
 */
export const PageLoader = ({ compact = false }) => {
  const { t } = useTranslation();
  const loader = useLoader();
  const showLoader = loader?.showLoader;
  const hideLoader = loader?.hideLoader;

  useEffect(() => {
    if (!showLoader) return undefined;
    showLoader();
    return hideLoader;
  }, [showLoader, hideLoader]);

  if (compact) return null;
  // keeps the page area's place while the chunk loads
  return (
    <Box
      role="progressbar"
      aria-label={t("loadingPage")}
      sx={{ minHeight: 240, width: "100%" }}
    />
  );
};

/**
 * Suspense boundary for lazy pages, around a layout's <Outlet /> so the
 * layout (sidebar, header) stays on screen while the page chunk loads.
 */
const PageSuspense = ({ children, compact = false }) => (
  <Suspense fallback={<PageLoader compact={compact} />}>{children}</Suspense>
);

export default PageSuspense;
